import React from "react";
import { getLocalizedString } from "@icarius-localization/strings";
import { CloseIcon } from "@icarius-icons";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Checkbox
} from "@material-ui/core";
import DialogTitleDivider from "@icarius-common/dialogTitleDivider";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import PaperDraggable from "@icarius-common/paperDraggable";
import DialogTransition from "@icarius-common/dialogTransition";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { useDispatch } from "react-redux";
import Loader from "@icarius-common/loader";
import { createFieldAction, modifyFieldAction } from "@icarius-pages/catalogs/actions";
import useHandleForm from "./useHandleForm";
import CustomNumberFormat from '@icarius-common/dynamicInputs/customNumberFormat';

const gridStyle = { height: 75, padding: "10px 0px" };

const CreateModifyTableDialog = (props) => {

  const {
    open,
    isLoading,
    data,
    handleClose,
    typeList,
    subTypeList,
    selectedRow,
  } = props;

  const dispatch = useDispatch();

  const create = (dataToSend) => {
    dispatch(createFieldAction({ ...dataToSend, tableCode: selectedRow.physicalCode }))
      .then((res) => res?.status === 200 && handleClose());
  }

  const modify = (dataToSend) => {
    dispatch(modifyFieldAction({ tableCode: selectedRow.physicalCode, fieldCode: dataToSend.fieldCode, name: dataToSend.name, size: dataToSend.size, notNull: dataToSend.notNull, default: dataToSend.default, subType: dataToSend.subType }))
      .then((res) => res?.status === 200 && handleClose());
  }

  const openValidationError = () => {
    dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
  }

  const {
    formData,
    setFormValue,
    submit,
    isCreate,
  } = useHandleForm(data, create, modify, openValidationError);

  return (
    <Dialog
      open={open}
      TransitionComponent={DialogTransition}
      PaperComponent={PaperDraggable}
      maxWidth={"sm"}
      fullWidth={true}
    >
      <div className={"dialog-container"}>
        <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
          {isCreate ? getLocalizedString("createField") : getLocalizedString("modifyField")}
          <DialogTitleDivider />
        </DialogTitle>
        <CloseIcon className={"dialog-close-icon icon"} onClick={handleClose} />
        <DialogContent>
          <Loader open={isLoading} />
          <Grid container item xs={12}>
            <Grid container item alignItems="center" xs={12} style={gridStyle}> {/* Código */}
              <TextField
                required
                disabled={!isCreate}
                label={getLocalizedString("codeShort")}
                fullWidth
                margin={"none"}
                value={formData.fieldCode}
                onChange={(e) => setFormValue(e.target.value, "fieldCode")} helperText={
                  <>
                    {getLocalizedString("allFieldsMustBeginWithU")}
                  </>
                }
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} style={gridStyle}> {/* Nombre */}
              <TextField
                required
                label={getLocalizedString("name")}
                fullWidth
                margin={"none"}
                value={formData.name}
                onChange={(e) => setFormValue(e.target.value, "name")}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={gridStyle}> {/* Tipo */}
              <FormControl style={{ width: "100%" }}>
                <InputLabel required id={`label-type`}>{getLocalizedString("type")}</InputLabel>
                <Select
                  disabled={!isCreate}
                  value={formData.type}
                  labelId={`label-type`}
                  id={`select-type`}
                  onChange={(e) => setFormValue(e.target.value, "type")}
                  margin={"none"}
                >
                  {
                    typeList.map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} sm={6} style={{ ...gridStyle, paddingLeft: 4 }} > {/* Subtipo */}
              < FormControl style={{ width: "100%" }}>
                <InputLabel required={formData.type === "B"} id={`label-subType`}>{getLocalizedString("subtype")}</InputLabel>
                <Select
                  disabled={!isCreate || (formData.type !== "N" && formData.type !== "B")}
                  value={formData.subType}
                  labelId={`label-subType`}
                  id={`select-subType`}
                  onChange={(e) => setFormValue(e.target.value, "subType")}
                  margin={"none"}
                >
                  {
                    subTypeList.filter(el => el.type === formData.type).map(item => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}>
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} style={gridStyle}> {/* Largo */}
              <TextField
                required
                disabled={(formData.type === "D" || formData.type === "M" || formData.type === "B" || (formData.type === "N" && formData.subType === "T"))}
                label={getLocalizedString("size")}
                fullWidth
                margin={"none"}
                value={formData.size}
                onChange={(e) => setFormValue(e.target.value, "size")}
                InputProps={{ inputComponent: CustomNumberFormat }}
              />
            </Grid>
            <Grid container item alignItems="center" xs={12} style={gridStyle}> {/* Not null */}
              <div style={{ display: "flex", alignItems: "center", cursor: "pointer", paddingRight: 20 }} onClick={() => setFormValue(!Boolean(formData.notNull), "notNull")}>
                <Typography className="whiteText" variant="subtitle1" style={{ display: "inline-block" }}>
                  {getLocalizedString("notNull")}
                </Typography>
                <Checkbox checked={formData.notNull} />
              </div>
            </Grid>
            <Grid container item alignItems="center" xs={12} style={gridStyle}> {/* Default */}
              <TextField
                disabled={!isCreate || formData.type === "M"}
                label={getLocalizedString("defaultValue")}
                fullWidth
                margin={"none"}
                value={formData.default}
                onChange={(e) => setFormValue(e.target.value, "default")}
              />
            </Grid>
          </Grid>

        </DialogContent>
        <DialogActions>
          <ButtonDialogAction onClick={handleClose} text={getLocalizedString("cancel")} />
          <ButtonDialogAction onClick={submit} isAccept text={getLocalizedString("accept")} />
        </DialogActions>
      </div>
    </Dialog >
  );
}

export default CreateModifyTableDialog;